import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { ProductsContext } from '../../context/products/ProductContext';
import Paginator from '../../components/ui/Paginator';

// Las categorías CABYS tienen 7 niveles; al seleccionar una del último nivel se cargan sus productos
const LAST_LEVEL = 7;
const PAGE_SIZE = 10;

type CategoryNode = {
  categoria: string;
  descripcion: string;
  nivel: number;
  children?: CategoryNode[]; // undefined = aún no se ha cargado
};

type CabysProduct = {
  categoria: string;
  descripcion: string;
  Impuesto: number;
};

const toNodes = (list: any[], nivel: number): CategoryNode[] =>
  (list ?? []).map((cat) => ({ categoria: cat.categoria, descripcion: cat.descripcion, nivel }));

const nodeKey = (node: CategoryNode) => `${node.nivel}-${node.categoria}`;

const SearchByCategory = ({ onSelect }) => {
  const { getPrincipalCabys, getCabysByCategory, getCabysByCategory8 } = useContext(ProductsContext);

  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [loadingTree, setLoadingTree] = useState(false);

  const [selectedPath, setSelectedPath] = useState<CategoryNode[]>([]);
  const [products, setProducts] = useState<CabysProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [filter, setFilter] = useState('');
  const [pageActual, setPageActual] = useState(1);
  const productsRequestRef = useRef(0);

  useEffect(() => {
    const loadCategories = async () => {
      setLoadingTree(true);
      const response = await getPrincipalCabys();
      setCategories(toNodes(response as any[], 1));
      setLoadingTree(false);
    };
    loadCategories();
  }, []);

  useEffect(() => {
    setPageActual(1);
  }, [filter, selectedPath]);

  // Reemplaza los hijos de un nodo dentro del árbol
  const setNodeChildren = (tree: CategoryNode[], target: CategoryNode, children: CategoryNode[]): CategoryNode[] =>
    tree.map((node) => {
      if (nodeKey(node) === nodeKey(target)) return { ...node, children };
      if (node.children) return { ...node, children: setNodeChildren(node.children, target, children) };
      return node;
    });

  const handleNodeClick = async (node: CategoryNode, path: CategoryNode[]) => {
    const key = nodeKey(node);

    // Último nivel: se cargan los productos de la categoría
    if (node.nivel === LAST_LEVEL) {
      setSelectedPath(path);
      setFilter('');
      setLoadingProducts(true);
      const requestId = ++productsRequestRef.current;
      const response = await getCabysByCategory8(node.categoria);
      if (requestId !== productsRequestRef.current) return;
      setProducts((response as any[]) ?? []);
      setLoadingProducts(false);
      return;
    }

    const isOpen = !!expanded[key];
    setExpanded({ ...expanded, [key]: !isOpen });

    // Los hijos se cargan una sola vez
    if (!isOpen && node.children === undefined) {
      setLoadingKey(key);
      const response = await getCabysByCategory(node.nivel + 1, node.categoria as any);
      setCategories((prev) => setNodeChildren(prev, node, toNodes(response as any[], node.nivel + 1)));
      setLoadingKey(null);
    }
  };

  const filteredProducts = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return products;
    return products.filter((p) =>
      p.descripcion?.toLowerCase().includes(term) || String(p.categoria).includes(term));
  }, [products, filter]);

  const totalPage = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const pageItems = filteredProducts.slice((pageActual - 1) * PAGE_SIZE, pageActual * PAGE_SIZE);

  const selectProduct = (p: CabysProduct) =>
    onSelect({ CodigoBienServicio: p.categoria, DescripcionBienServicio: p.descripcion, Impuesto: p.Impuesto });

  const renderTree = (nodes: CategoryNode[], parentPath: CategoryNode[] = []): JSX.Element[] =>
    nodes.map((node) => {
      const key = nodeKey(node);
      const path = [...parentPath, node];
      const isLeaf = node.nivel === LAST_LEVEL;
      const isOpen = !!expanded[key];
      const isActive = isLeaf && selectedPath.length > 0 && nodeKey(selectedPath[selectedPath.length - 1]) === key;

      return (
        <li key={key}>
          <div
            className={`d-flex align-items-start rounded pe-2 py-1 small ${isActive ? 'bg-primary-subtle text-primary-emphasis fw-semibold' : ''}`}
            style={{ ...styles.treeItem, paddingLeft: 4 + (node.nivel - 1) * 14 }}
            onClick={() => handleNodeClick(node, path)}
            title={node.descripcion}
          >
            <span style={styles.treeToggle}>
              {loadingKey === key
                ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                : !isLeaf && <i className={`bi ${isOpen ? 'bi-chevron-down' : 'bi-chevron-right'}`} />}
            </span>
            <i className={`bi ${isLeaf ? 'bi-tag' : isOpen ? 'bi-folder2-open' : 'bi-folder'} me-2 text-muted`} />
            <span className="flex-grow-1">{node.descripcion}</span>
          </div>
          {isOpen && node.children && (
            node.children.length > 0
              ? <ul className="list-unstyled mb-0">{renderTree(node.children, path)}</ul>
              : <div className="text-muted small fst-italic py-1" style={{ paddingLeft: 40 + node.nivel * 14 }}>Sin subcategorías</div>
          )}
        </li>
      );
    });

  const renderProductsBody = () => {
    if (loadingProducts) {
      return (
        <tr><td colSpan={4} className="text-center py-4">
          <span className="spinner-border spinner-border-sm me-2" /> Cargando productos...
        </td></tr>
      );
    }
    if (selectedPath.length === 0) {
      return (
        <tr><td colSpan={4} className="text-center py-4 text-muted">
          <i className="bi bi-diagram-3 me-2" />Navegue por las categorías y seleccione una del último nivel para ver sus productos.
        </td></tr>
      );
    }
    if (filteredProducts.length === 0) {
      return (
        <tr><td colSpan={4} className="text-center py-4 text-muted">
          {filter ? `No se encontraron productos para "${filter.trim()}".` : 'Esta categoría no tiene productos.'}
        </td></tr>
      );
    }
    return pageItems.map((p) => (
      <tr key={p.categoria} style={{ cursor: 'pointer' }} onClick={() => selectProduct(p)}>
        <td className="text-muted text-nowrap" style={{ fontFamily: 'monospace' }}>{p.categoria}</td>
        <td>{p.descripcion}</td>
        <td className="text-center">
          <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">{p.Impuesto}%</span>
        </td>
        <td className="text-center">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            title="Seleccionar"
            onClick={(e) => { e.stopPropagation(); selectProduct(p); }}
          >
            <i className="bi bi-check-lg me-1" />Seleccionar
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <div className="row g-3">
      {/* Árbol de categorías */}
      <div className="col-12 col-lg-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-body">
            <h6 className="mb-3">Categorías</h6>
            <div style={styles.treeContainer}>
              {loadingTree ? (
                <div className="text-center py-4 small">
                  <span className="spinner-border spinner-border-sm me-2" /> Cargando categorías...
                </div>
              ) : categories.length > 0 ? (
                <ul className="list-unstyled mb-0">{renderTree(categories)}</ul>
              ) : (
                <p className="text-muted small mb-0">No se encontraron categorías.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Productos de la categoría seleccionada */}
      <div className="col-12 col-lg-8">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-body">
            <h6 className="mb-2">Productos</h6>

            {selectedPath.length > 0 && (
              <div className="d-flex flex-wrap align-items-center gap-1 small text-muted mb-3">
                {selectedPath.map((node, i) => (
                  <span key={nodeKey(node)} className={i === selectedPath.length - 1 ? 'fw-semibold text-body' : ''}>
                    {node.descripcion}
                    {i < selectedPath.length - 1 && <i className="bi bi-chevron-right ms-1" style={{ fontSize: 10 }} />}
                  </span>
                ))}
              </div>
            )}

            {selectedPath.length > 0 && products.length > 0 && (
              <div className="input-group mb-3">
                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filtrar por código o descripción..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                {filter && (
                  <button className="btn btn-outline-secondary" type="button" title="Limpiar" onClick={() => setFilter('')}>
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr className="text-muted small text-uppercase">
                    <th style={{ width: 170 }}>Código</th>
                    <th>Descripción</th>
                    <th className="text-center" style={{ width: 100 }}>Impuesto</th>
                    <th className="text-center" style={{ width: 140 }}>Acción</th>
                  </tr>
                </thead>
                <tbody>{renderProductsBody()}</tbody>
              </table>
            </div>

            {!loadingProducts && filteredProducts.length > 0 && (
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className="text-muted small">{filteredProducts.length} productos</span>
                {totalPage > 1
                  ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} />
                  : <div />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const styles = {
  treeContainer: {
    maxHeight: '60vh',
    overflowY: 'auto' as const,
  },
  treeItem: {
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  treeToggle: {
    width: 16,
    minWidth: 16,
    display: 'inline-flex',
    justifyContent: 'center',
    marginRight: 4,
    marginTop: 2,
    color: '#6c757d',
  },
};

export default SearchByCategory;
